import { Button, PickerMap, Tag } from '@schuettflix/react-components';
import { useTranslation } from 'react-i18next';
import { WizardPanel } from '@/shared/components/Wizard.tsx';
import React from 'react';
import { environment } from '@/environment.ts';
import { GeoAddress, GeoAddressSchema } from '@/modules/location/domain/GeoAddressSchema.ts';
import { useMarketContext } from '@/shared/context/MarketContext.tsx';
import { MarketCode } from '@schuettflix/utils';
import { Controller } from 'react-hook-form';
import * as z from 'zod';
import { useZodForm } from '@/shared/hooks/useZodForm.ts';

const defaultLocations: Record<MarketCode, { latitude: number; longitude: number }> = {
    DE: { latitude: 51.9032524, longitude: 8.378543 },
    AT: { latitude: 48.2085211, longitude: 16.359983 },
    PL: { latitude: 52.1803943, longitude: 21.0105351 },
    CZ: { latitude: 50.0489371, longitude: 14.3541355 },
};

const FormGeoAddressSchema = z.object({
    geoAddress: z
        .object({
            address: z.object({
                formatted: z.string().min(1),
                placeId: z.string().min(1),
                country: z.string().min(1),
                state: z.string().min(1),
                city: z.string().min(1),
                street: z.string().nullable(),
                streetNumber: z.string().nullable(),
                zipCode: z.string().min(1),
            }),
            coordinates: z.object({
                latitude: z.number(),
                longitude: z.number(),
                openLocationCode: z.string().min(1),
            }),
        })
        .nullable()
        .refine(data => data !== null),
});

type FormGeoAddress = z.infer<typeof FormGeoAddressSchema>;

export type GeoAddressPanelProps = {
    onClose: () => void;
    onSubmit: (address: GeoAddress) => void;
};

export const GeoAddressPanel = (props: GeoAddressPanelProps) => {
    const { t, i18n } = useTranslation();
    const { marketCode } = useMarketContext();

    const toDomain = (formData: FormGeoAddress) => {
        return GeoAddressSchema.parse(formData.geoAddress);
    };

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useZodForm({
        schema: FormGeoAddressSchema.transform(toDomain).pipe(GeoAddressSchema),
        defaultValues: {
            geoAddress: null,
        },
    });

    return (
        <WizardPanel
            noMaximize
            noPadding
            onClose={props.onClose}
            title={t('location.newAddress.panel.title')}
            footer={<GeoAddressPanelFooter onClick={handleSubmit(props.onSubmit)} />}
        >
            <Controller
                control={control}
                name="geoAddress"
                render={({ field: { ref: _, ...field } }) => (
                    <div className="relative h-full w-full">
                        <PickerMap
                            className="h-full"
                            apiKey={environment.GOOGLE_MAPS_API_KEY}
                            language={i18n.language}
                            initialCoordinates={field.value?.coordinates ?? defaultLocations[marketCode]}
                            defaultMapType={'hybrid'}
                            showAddressPicker={true}
                            addressPickerLabel={t('location.newAddress.panel.mapPickerLabel')}
                            mapTypeLabels={{
                                mapTypeHeadline: t('location.mapTypeLabels.mapTypeHeadline'),
                                roadmap: t('location.mapTypeLabels.roadmap'),
                                satellite: t('location.mapTypeLabels.satellite'),
                                hybrid: t('location.mapTypeLabels.hybrid'),
                            }}
                            {...field}
                        />

                        {errors.geoAddress && (
                            <div className="@lg:px-6 absolute bottom-0 left-0 right-0 px-4 pb-8">
                                <Tag type={'error'} label={'Please select a valid address'} className="w-full" />
                            </div>
                        )}
                    </div>
                )}
            />
        </WizardPanel>
    );
};

interface GeoAddressPanelFooterProps {
    onClick: () => void;
}

export const GeoAddressPanelFooter: React.FC<GeoAddressPanelFooterProps> = (props: GeoAddressPanelFooterProps) => {
    const { t } = useTranslation();

    return (
        <div className="flex justify-end">
            <Button label={t('common.save')} size="sm" type="button" onClick={props.onClick} />
        </div>
    );
};
